import { Box, Slider, TextField } from '@mui/material'
import { Trans } from 'react-i18next'
import { ID, Port } from '../../types/models'
import { OptimizerControlPort, PortNumber, PortConfigOptimizerControl } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { ScheduleLineView } from '../../components/SchedulerLineView'
import { RelayPortControl } from './RelayPortControl'
import { LinkTo } from '../../components/atoms/LinkTo'

/**
 * Controller for optimizer control.
 */
export class OptimizerControlPortControl extends PortControl<OptimizerControlPort, PortConfigOptimizerControl> {

  port: OptimizerControlPort
  portMap: Record<string, PortNumber>

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as OptimizerControlPort
    this.portMap = {
      '211/0': '3/0' as PortNumber,
      '211/1': '3/1' as PortNumber,
      '211/2': '3/2' as PortNumber,
      '211/3': '3/3' as PortNumber,
      '211/4': '3/4' as PortNumber,
      '211/5': '103/0' as PortNumber,
      '211/6': '0/0' as PortNumber,
      '211/7': '0/0' as PortNumber,
    }
  }

  /**
   * Find the corresponding relay control.
   */
  relayControl(): RelayPortControl | null {
    const ctrl = this.deviceControl.findPortBy(portControl =>
      portControl.port.type === 'RELAY' &&
      (portControl as RelayPortControl).port.port_number === this.getConfig('port'))
    return ctrl
      ? ctrl as RelayPortControl
      : null
  }

  get group(): string { return this.app.t('PortGroup-Optimization') }

  getTypeName(): string { return this.app.t('PortType-Optimizer Control') }

  getName(): string {
    return this.app.t('PortName-Optimizer Control {number}').replace('{number}', `${this.number}`)
  }

  getDescription(): string {
    return this.app.t('Energy usage optimizer control port')
  }

  getConfigDefaults(): PortConfigOptimizerControl {

    if (!(this.port.port_number in this.portMap)) {
      throw new Error(`Optimizer port mapping is not defined for port '${this.port.port_number}'.`)
    }

    return {
      enabled: false,
      use_temperature_meas: false,
      use_clock_based_duty: false,
      use_fixed_price: false,
      rapid_switching_ok: false,
      good_sink: false,

      cont_dest: 0,
      cont_dest_kind: 'DEST_KIND_THIS_DEVICE',

      port: this.portMap[this.port.port_number],
      fixed_buy_price_threshold: 0,

      min_duty: 51,
      max_duty: 51,
      min_duty_at: 0,
      max_duty_at: 0,
      time_constant: 12,

      tempsens_dest: 0,
      tempsens_dest_kind: 'DEST_KIND_THIS_DEVICE',
      tempsens_port: '0/0' as PortNumber,

      min_temp: 0,
      abs_min_temp: 0,
      max_temp: 0,
      abs_max_temp: 0,
      cop_curve: [0, 0, 0, 0],

      pv_priority: 1,
      plan_by_price: false,

      fuse_priority: 0,
      phases: [true, true, true],
      appliance_power: 0
    }
  }

  infoTop() : React.ReactNode | undefined {
    const schedule = this.port.values.schedule || []
    const relayControl = this.relayControl()
    const relayPortId = relayControl?.port.id || 0 as ID

    return <Box>
      <Box sx={{ textAlign: 'center' }}>
        <LinkTo underline title={this.app.t('View relay config for this optimizer')} onClick={() => this.gotoPort(relayPortId)}/>
      </Box>

      <Trans>Current schedule</Trans>:
      <Box sx={{ ml: 2 }}>
        {schedule.length === 0 && <Trans>No schedule</Trans>}
        {schedule.map((line, idx) => <ScheduleLineView key={idx} line={line}/>)}
      </Box>
    </Box>
  }

  configBottomLeft() : React.ReactNode | undefined {

    const onChangeDuty = (value: [number, number]): void => {
      const [min, max] = value
      this.setConfig('min_duty', min, false)
      this.setConfig('max_duty', max)
    }

    return <Box data-cy="box-Minimum and Maximum Duty">
      <Trans>Minimum and Maximum Duty</Trans>
      <Slider
        data-cy="slider-Minimum and Maximum Duty"
        sx={{ ml: 2, mt: 6, width: '85%' }}
        size="medium"
        valueLabelDisplay="on"
        value={[this.getConfig('min_duty'), this.getConfig('max_duty')]}
        onChange={(_, value) => onChangeDuty(value as [number, number])}
        valueLabelFormat={(num) => `${num}%`}
      />
      <Box><Trans>Minimum Duty</Trans>: {this.getConfig('min_duty')} %</Box>
      <Box sx={{ mt: 2, ml: 2 }}><Trans>At Temperature</Trans>:
        <TextField
          data-cy='text-Minimum Duty At Temperature'
          margin="normal"
          sx={{ width: '30%', mt: -1, ml: 2 }}
          size="small"
          value={this.getConfig('min_duty_at')}
          disabled={this.getConfig('min_duty') === this.getConfig('max_duty')}
          inputProps={{ inputMode: 'numeric', pattern: '-[0-9]*' }}
          type="text"
          onChange={(e) => this.setConfig('min_duty_at', e.target.value)}
          onBlur={() => this.setConfig('min_duty_at',
            Number.isNaN(parseInt(this.getConfig('min_duty_at'))) ? 0 : parseInt(this.getConfig('min_duty_at'))
          )}
        /> °C
      </Box>
      <Box><Trans>Maximum Duty</Trans>: {this.getConfig('max_duty')} %</Box>
      <Box sx={{ mt: 2, ml: 2 }}><Trans>At Temperature</Trans>:
        <TextField
          data-cy='text-Maximum Duty At Temperature'
          margin="normal"
          sx={{ width: '30%', mt: -1, ml: 2 }}
          size="small"
          value={this.getConfig('max_duty_at')}
          disabled={this.getConfig('min_duty') === this.getConfig('max_duty')}
          inputProps={{ inputMode: 'numeric', pattern: '-[0-9]*' }}
          type="text"
          onChange={(e) => this.setConfig('max_duty_at', e.target.value)}
          onBlur={() => this.setConfig('max_duty_at',
            Number.isNaN(parseInt(this.getConfig('max_duty_at'))) ? 0 : parseInt(this.getConfig('max_duty_at'))
          )}
        /> °C
      </Box>
    </Box>
  }

  configBottomRight() : React.ReactNode | undefined {

    const onChangeOptByPrice = () => {
      this.setConfig('plan_by_price', !this.getConfig('plan_by_price'))
    }

    const onChangePriority = (priority: number) => {
      this.setConfig('pv_priority', priority)
    }

    const onChangeTimeConstant = (time: number) => {
      this.setConfig('time_constant', time)
    }

    return <Box>
      <Trans>Other Parameters</Trans>
      {!this.app.userHas('PRICE_OPTIMIZATION') && <Box sx={{ mb: -1, mt: 1, color: 'error.light', fontSize: '90%' }}><Trans>This feature requires subscription.</Trans></Box>}
      <Box sx={{ color: this.app.userHas('PRICE_OPTIMIZATION') ? 'inherit' : 'gray' }}><Trans>Optimize by Price</Trans>
        <CustomSwitch
          sx={{ ml: 2 }}
          data-cy="switch-Optimize by Price"
          disabled={!this.app.userHas('PRICE_OPTIMIZATION')}
          onClick={onChangeOptByPrice}
          checked={this.getConfig('plan_by_price')}
        />
      </Box>
      <Box>
        <Trans>PV Priority</Trans>
        <Slider
          data-cy="box-PV Priority"
          sx={{ ml: 2, mt: 6, width: '85%' }}
          size="medium"
          valueLabelDisplay="on"
          value={this.getConfig('pv_priority')}
          min={0}
          max={15}
          onChange={(_, value) => onChangePriority(value as number)}
          valueLabelFormat={(num) => `${num}`}
        />
      </Box>

      <Box>
        <Trans>Time Constant</Trans>
        <Slider
          data-cy="box-Time Constant"
          sx={{ ml: 2, mt: 6, width: '85%' }}
          size="medium"
          valueLabelDisplay="on"
          value={this.getConfig('time_constant')}
          min={2}
          max={48}
          onChange={(_, value) => onChangeTimeConstant(value as number)}
          valueLabelFormat={(num) => `${num}h`}
        />
      </Box>
    </Box>
  }

  get isVisible() {
    return this.app.userHas('OPTIMIZER_CONTROL')
  }
}
